// src/context/CartContext.jsx
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { trackAddToCart } from '../utils/analytics';

const CartContext = createContext(null);

// ── Reducer ───────────────────────────────────────────────────────────────
const STORAGE_KEY = 'ke_cart';

const initialState = {
  items:          [],    // [{ product, name, emoji, price, mrp, qty }]
  couponCode:     null,
  couponDiscount: 0,
};

function cartReducer(state, action) {
  switch (action.type) {

    case 'ADD': {
      const exists = state.items.find(i => i.product === action.item.product);
      const items  = exists
        ? state.items.map(i =>
            i.product === action.item.product
              // Merge the latest selection (e.g. a newly picked colour) and bump qty.
              ? { ...i, ...action.item, qty: i.qty + (action.item.qty || 1) }
              : i
          )
        : [...state.items, { ...action.item, qty: action.item.qty || 1 }];
      return { ...state, items };
    }

    case 'REMOVE':
      // Cart contents changed → an applied coupon may no longer qualify
      // (min-order value, etc). Clear it so the shown total can't go stale or
      // negative and checkout can't dead-end on server re-validation; the
      // customer simply re-applies it against the new cart.
      return { ...state, items: state.items.filter(i => i.product !== action.productId), couponCode: null, couponDiscount: 0 };

    case 'UPDATE_QTY': {
      const items = action.qty < 1
        ? state.items.filter(i => i.product !== action.productId)
        : state.items.map(i => i.product === action.productId ? { ...i, qty: action.qty } : i);
      // Changing quantity can invalidate the coupon too → clear it (see REMOVE).
      return { ...state, items, couponCode: null, couponDiscount: 0 };
    }

    case 'APPLY_COUPON':
      return { ...state, couponCode: action.code, couponDiscount: action.discount };

    case 'REMOVE_COUPON':
      return { ...state, couponCode: null, couponDiscount: 0 };

    case 'CLEAR':
      return { ...initialState };

    case 'HYDRATE':
      return { ...initialState, ...action.payload };

    default:
      return state;
  }
}

// ── Provider ──────────────────────────────────────────────────────────────
export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, initialState, (init) => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...init, ...JSON.parse(saved) } : init;
    } catch { return init; }
  });

  // Persist cart to localStorage on every change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // ── Derived values ──────────────────────────────────────────────────────
  const itemCount     = state.items.reduce((s, i) => s + i.qty, 0);
  const subtotal      = state.items.reduce((s, i) => s + i.price * i.qty, 0);
  const mrpTotal      = state.items.reduce((s, i) => s + i.mrp  * i.qty, 0);
  const productDiscount = mrpTotal - subtotal;
  const deliveryFee   = subtotal >= 5000 ? 0 : 99;
  const grandTotal    = Math.max(0, subtotal - state.couponDiscount + deliveryFee);
  const totalSavings  = productDiscount + state.couponDiscount;

  // ── Actions ─────────────────────────────────────────────────────────────
  const addToCart = useCallback((product, qty = 1, opts = {}) => {
    dispatch({
      type: 'ADD',
      item: {
        product: product._id,
        name:    product.name,
        emoji:   product.emoji,
        image:   product.images?.[0] || null,
        price:   product.price,
        mrp:     product.mrp,
        brand:   product.brand,
        // Chosen finish + the model's storage label, for the cart/order lines.
        color:   opts.color || product.colors?.[0] || null,
        variantLabel: product.variantLabel || null,
        qty,
      },
    });
    trackAddToCart(product, qty);
  }, []);

  const removeFromCart = useCallback((productId) => {
    dispatch({ type: 'REMOVE', productId });
  }, []);

  const updateQty = useCallback((productId, qty) => {
    dispatch({ type: 'UPDATE_QTY', productId, qty });
  }, []);

  const applyCoupon = useCallback((code, discount) => {
    dispatch({ type: 'APPLY_COUPON', code, discount });
  }, []);

  const removeCoupon = useCallback(() => {
    dispatch({ type: 'REMOVE_COUPON' });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  const isInCart = useCallback((productId) => {
    return state.items.some(i => i.product === productId);
  }, [state.items]);

  const getQty = useCallback((productId) => {
    return state.items.find(i => i.product === productId)?.qty || 0;
  }, [state.items]);

  /** Build the payload expected by POST /api/orders */
  const getOrderPayload = useCallback((address, paymentMethod) => ({
    items:         state.items.map(i => ({ product: i.product, qty: i.qty, color: i.color || undefined })),
    address,
    paymentMethod,
    couponCode:    state.couponCode || undefined,
  }), [state]);

  const value = {
    // State
    items:           state.items,
    couponCode:      state.couponCode,
    couponDiscount:  state.couponDiscount,
    // Derived
    itemCount,
    subtotal,
    mrpTotal,
    productDiscount,
    deliveryFee,
    grandTotal,
    totalSavings,
    // Actions
    addToCart,
    removeFromCart,
    updateQty,
    applyCoupon,
    removeCoupon,
    clearCart,
    isInCart,
    getQty,
    getOrderPayload,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
